import * as React from 'react';
import { useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import styled from 'styled-components';
import { useIntl } from 'react-intl-next';
import { di } from 'react-magnetic-di';
import { useQueryParam } from 'react-resource-router';
import type { exitTestModeAction } from 'state/actions/login';
import InlineMessage from '@atlaskit/inline-message';
import { token } from '@atlaskit/tokens';
import { ThemedButton } from '@atlassian/help-center-common-component/themed-button';
import { QUERY_PARAM_SSO_TEST } from '@atlassian/help-center-common-util/login/constant';
import { isTestModeRequested } from '@atlassian/help-center-common-util/url/url';
import messages from './messages';

export interface Props {
    email: string;
    onExitTestMode: typeof exitTestModeAction;
}

export const SsoTestModeBanner = (props: Props) => {
    di(isTestModeRequested);
    const { formatMessage } = useIntl();
    const [ssoTest] = useQueryParam(QUERY_PARAM_SSO_TEST);
    const { email, onExitTestMode } = props;

    const exitTestModeHandler = useCallback(() => {
        onExitTestMode(email);
    }, [email, onExitTestMode]);

    const isTestModeRequestedHandler = useMemo(() => {
        return isTestModeRequested(ssoTest);
    }, [ssoTest]);

    const exitTestModeLinkProvider = useCallback(
        (chunks: ReactNode) => {
            return (
                <ThemedButton
                    appearance="link"
                    onClick={exitTestModeHandler}
                    actionSubjectId="exitTestModeLink"
                    spacing="none"
                >
                    {chunks}
                </ThemedButton>
            );
        },
        [exitTestModeHandler]
    );

    return (
        <>
            {isTestModeRequestedHandler && (
                <InlineMessageContainer>
                    <InlineMessage appearance="warning" title={formatMessage(messages.ssoTestModeInlineMessageTitle)}>
                        {formatMessage(messages.ssoTestModeInlineMessageContent, { a: exitTestModeLinkProvider })}
                    </InlineMessage>
                </InlineMessageContainer>
            )}
        </>
    );
};

export default SsoTestModeBanner;

const inlineMessageContainerBackgroundColor = '#fff7d6';

const InlineMessageContainer = styled.div`
    padding: ${token('space.100', '8px')} ${token('space.800', '64px')};
    display: flex;
    justify-content: center;
    background-color: ${inlineMessageContainerBackgroundColor};
`;
